import React, { useState, useEffect, useMemo } from 'react';
import LoadingOverlay from '../components/overlays/LoadingOverlay';
import AlertModal from '../components/modals/AlertModal';
import CornerNotice from '../components/notifications/CornerNotice';
import FormEditorPreview from '../components/forms/FormEditorPreview';
import RichTextEditor from '../components/forms/RichTextEditor';
import './FormsManager.css';
import {
  FORM_RELATIONSHIP_TYPES,
  FORM_RELATIONSHIP_TYPE_LABELS,
  FORM_RELATIONSHIP_TYPE_HINTS,
  formatFormRelationshipTypeLabel,
  normalizeFormRelationshipType
} from '../constants/formRelationships';
import {
  FORM_ASSIGNMENT_METADATA_KEYS,
  normalizeFormAssignmentMetadata,
  buildFormAssignmentMetadataForSave,
  defaultFormAssignmentMetadata
} from '../utils/formAssignmentMetadata';
import {
  FORM_TYPES,
  FORM_TYPE_LABELS,
  ANSWER_MODES,
  ANSWER_MODE_LABELS,
  TEXT_MATCH_TYPES,
  TEXT_MATCH_LABELS,
  CHOICE_QUESTION_TYPES,
  TEXT_QUESTION_TYPES,
  NUMBER_QUESTION_TYPES,
  DATE_QUESTION_TYPES,
  UPLOAD_QUESTION_TYPES,
  defaultScoringForQuestion,
  defaultNumberRule,
  defaultDateRule,
  getAnswerModesForQuestionType,
  normalizeLegacyFormType,
  compactQuestionForStorage
} from '../constants/formScoring';
import { isPointsForm, validateQuestionScoring } from '../utils/formScoring';
import { FORMS_TAXONOMY_SCOPES } from '../utils/formsPage';
import { richTextIsEmpty, stripHtmlToText, looksLikeHtml, labelPlainInputValue } from '../utils/richText';

const PROMPT_ALERT = {
  presentation: 'prompt',
  overlayOpacity: 0.16,
  overlayColor: '15, 23, 42',
  zIndex: 2000
};

const ASSIGNMENT_MAX_ATTEMPTS_MSG = {
  MUST_BE_ONE: 'When Allow resubmission is off, max attempts must be 1.',
  MUST_BE_OVER_ONE: 'When Allow resubmission is enabled, max attempts must be greater than 1.'
};

const ASSIGNMENT_WARNING_NOTICE = {
  position: 'bottom-left',
  containerColor: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)',
  textColor: '#92400e',
  showOverlay: false,
  dismissOnClick: true,
  autoClose: true,
  viewTime: 4500
};

const QUESTION_TYPE_OPTIONS = [
  {
    value: 'varchar',
    label: 'Short answer',
    description: 'Single-line text for names, titles, or brief responses.'
  },
  {
    value: 'textarea',
    label: 'Long answer',
    description: 'Multi-line text area for paragraphs, explanations, or essays.'
  },
  {
    value: 'number',
    label: 'Number',
    description: 'Numeric input for quantities, scores, or measurements.'
  },
  {
    value: 'date',
    label: 'Date',
    description: 'Calendar date picker (mm/dd/yyyy).'
  },
  {
    value: 'select',
    label: 'Single choice (Dropdown UI)',
    description: 'One option chosen from a compact dropdown menu.'
  },
  {
    value: 'single-choice',
    label: 'Single choice',
    description: 'Options shown inline; respondent picks exactly one.'
  },
  {
    value: 'multiple-choice',
    label: 'Multiple choice',
    description: 'Checkboxes for selecting one or more options.'
  },
  {
    value: 'file-upload',
    label: 'File upload',
    description: 'Attach one or more files — ideal for manual grading.'
  }
];

function getQuestionTypeOption(type) {
  return (
    QUESTION_TYPE_OPTIONS.find((opt) => opt.value === type) ||
    QUESTION_TYPE_OPTIONS[0]
  );
}

const FormsLinkIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const FormsGroupIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const FormsInfoIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4" />
    <path d="M12 8h.01" />
  </svg>
);

const DEFAULT_QUESTION_GROUP = 'Group 1';

function normalizeFormMetadata(raw) {
  const meta = raw && typeof raw === 'object' ? raw : {};
  return {
    groupingEnabled: meta.groupingEnabled === true,
    questionGroups: Array.isArray(meta.questionGroups)
      ? meta.questionGroups.map(String).filter(Boolean)
      : [],
    assignmentDefaults: normalizeFormAssignmentMetadata(meta.assignmentDefaults)
  };
}

function deriveQuestionGroups(questions, metadata) {
  if (metadata?.questionGroups?.length) {
    return [...metadata.questionGroups];
  }
  const fromQuestions = [...new Set((questions || []).map((q) => q.group).filter(Boolean))];
  return fromQuestions.length ? fromQuestions : [DEFAULT_QUESTION_GROUP];
}

function isGroupingEnabled(metadata, questions) {
  if (metadata && typeof metadata.groupingEnabled === 'boolean') {
    return metadata.groupingEnabled;
  }
  const groups = new Set((questions || []).map((q) => q.group).filter(Boolean));
  return groups.size > 1;
}

// UUID generator function
const generateUUID = () => {
  try {
    if (window && window.crypto && window.crypto.randomUUID) {
      return window.crypto.randomUUID();
    }
  } catch (_e) {}
  // Fallback UUID v4 generator
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

export function FormEditor({ formId, onBack, onSaved, onNotice, focus, formsCaps = {}, fetchWithAuth }) {
  const authFetch = fetchWithAuth || ((url, opts) => fetch(url, opts));
  const isEditing = formId && formId !== 'new';
  const id = isEditing ? formId : null;

  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [alertModal, setAlertModal] = useState(null);
  const [cornerNotice, setCornerNotice] = useState(null);
  const [removeRelConfirm, setRemoveRelConfirm] = useState(null);
  const [deleteQuestionIndex, setDeleteQuestionIndex] = useState(null);
  const [activeGroupTab, setActiveGroupTab] = useState(null);
  const [editingGroupTab, setEditingGroupTab] = useState(null);
  const [editGroupTabValue, setEditGroupTabValue] = useState('');
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const [newGroupTabValue, setNewGroupTabValue] = useState('');
  const [showEditorHelp, setShowEditorHelp] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTab, setPreviewTab] = useState('form');

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    primary_taxonomy_id: '',
    type: FORM_TYPES.SURVEY,
    questions: [],
    metadata: {
      groupingEnabled: false,
      questionGroups: [],
      assignmentDefaults: defaultFormAssignmentMetadata()
    }
  });
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(false);
  const [showSubjectPicker, setShowSubjectPicker] = useState(false);
  const [selectedTaxonomy, setSelectedTaxonomy] = useState(null);
  const [relationships, setRelationships] = useState([]);
  const [users, setUsers] = useState([]);
  const [groupsPeriods, setGroupsPeriods] = useState([]);
  const [showAssignmentsModal, setShowAssignmentsModal] = useState(false);
  const [showAddRelationshipModal, setShowAddRelationshipModal] = useState(false);
  const [newRelationship, setNewRelationship] = useState({
    parent_table: 'users',
    parent_id: '',
    relationship_type: FORM_RELATIONSHIP_TYPES.FILL_OUT,
    allowViewAnswersAfterSubmission: false,
    allowSeeWrongAnswersAfterSubmission: false,
    allowViewPointsEarnedAfterSubmission: true,
    assigneePublicRead: true,
    assigneePublicWrite: true,
    assigneeFlagsRead: true,
    assigneeMarkAddressed: true
  });
  const [isLoadingRelationships, setIsLoadingRelationships] = useState(false);
  const [hasSubmissions, setHasSubmissions] = useState(false);
  const [submissionCount, setSubmissionCount] = useState(0);

  const showFormAlert = (subject, body, variant = 'error', details = '') => {
    setAlertModal({
      variant,
      subject,
      body,
      details: details || undefined,
      actions: [{ label: 'OK', onClick: () => setAlertModal(null), variant: 'primary' }]
    });
  };

  const showAssignmentCornerNotice = (text) => {
    setCornerNotice({
      isOpen: true,
      ...ASSIGNMENT_WARNING_NOTICE,
      text
    });
  };

  useEffect(() => {
    if (isEditing) {
      loadForm();
      loadRelationships();
      checkFormSubmissions();
    } else {
      setFormData({
        title: '',
        description: '',
        primary_taxonomy_id: '',
        type: FORM_TYPES.SURVEY,
        questions: [],
        metadata: {
      groupingEnabled: false,
      questionGroups: [],
      assignmentDefaults: defaultFormAssignmentMetadata()
    }
      });
      setRelationships([]);
      setHasSubmissions(false);
      setSubmissionCount(0);
      setActiveGroupTab(null);
      setEditingGroupTab(null);
      setShowNewGroupInput(false);
      setSelectedTaxonomy(null);
      setShowSubjectPicker(false);
    }
    loadTaxonomyOptions();
    loadUsers();
    loadGroupsPeriods();
  }, [formId]);

  const loadTaxonomyOptions = async (selectedId = '') => {
    setTaxonomyLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedId) params.set('selected_id', String(selectedId));
      const response = await authFetch(`/api/forms/subject-taxonomy?${params.toString()}`);
      const data = await response.json();
      if (data.success) {
        setTaxonomyOptions(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load subject taxonomy:', error);
    } finally {
      setTaxonomyLoading(false);
    }
  };

  const openSubjectPicker = () => {
    loadTaxonomyOptions(formData.primary_taxonomy_id || '');
    setShowSubjectPicker(true);
  };

  const selectSubjectTaxonomy = (item) => {
    if (!item?.selectable) return;
    setSelectedTaxonomy({
      id: item.id,
      title: item.title || item.short_title || 'Subject',
      organization_group_name: item.organization_group_name || null,
      is_other_organization: item.is_other_organization === true
    });
    handleFormChange('primary_taxonomy_id', item.id);
    setShowSubjectPicker(false);
  };

  const clearSubjectTaxonomy = () => {
    setSelectedTaxonomy(null);
    handleFormChange('primary_taxonomy_id', '');
    setShowSubjectPicker(false);
  };

  useEffect(() => {
    if (focus !== 'assignments' || !isEditing || isLoading) return;
    setShowAssignmentsModal(true);
  }, [focus, isEditing, isLoading]);

  const checkFormSubmissions = async () => {
    if (!id) return { hasSubmissions: false, count: 0 };
    
    try {
      const response = await fetch(`/api/form-submissions?form_id=${id}`);
      const data = await response.json();
      
      if (data.success) {
        const submissions = data.data || [];
        const count = submissions.length;
        const hasSubs = count > 0;
        setSubmissionCount(count);
        setHasSubmissions(hasSubs);
        return { hasSubmissions: hasSubs, count };
      }
      return { hasSubmissions: false, count: 0 };
    } catch (error) {
      console.error('Failed to check form submissions:', error);
      // Don't block form loading if check fails
      return { hasSubmissions: false, count: 0 };
    }
  };

  const loadForm = async () => {
    setIsLoading(true);
    try {
      const response = await authFetch(`/api/forms/${id}`);
      const data = await response.json();
      
      if (data.success) {
        const form = data.data;
        let questions = typeof form.questions === 'string' 
          ? JSON.parse(form.questions) 
          : form.questions || [];
        
        // Ensure all questions have question_id (generate for missing ones)
        questions = questions.map(q => ({
          ...q,
          question_id: q.question_id || generateUUID()
        }));
        
        const metadata = normalizeFormMetadata(
          typeof form.metadata === 'string' ? JSON.parse(form.metadata) : form.metadata
        );
        const groups = deriveQuestionGroups(questions, metadata);

        setFormData({
          title: form.title || '',
          description: form.description || '',
          primary_taxonomy_id: form.primary_taxonomy_id || '',
          type: normalizeLegacyFormType(form.type),
          questions,
          metadata
        });
        if (form.primary_taxonomy_id) {
          setSelectedTaxonomy({
            id: form.primary_taxonomy_id,
            title: form.primary_taxonomy_name || 'Subject',
            organization_group_name: form.primary_taxonomy_organization_name || null,
            is_other_organization: form.primary_taxonomy_is_other_organization === true
          });
          loadTaxonomyOptions(form.primary_taxonomy_id);
        } else {
          setSelectedTaxonomy(null);
        }
        setActiveGroupTab(groups[0] || null);
      } else {
        throw new Error(data.message || 'Failed to load form');
      }
    } catch (error) {
      showFormAlert('Load failed', 'Failed to load form', 'error', error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const loadRelationships = async () => {
    if (!id) return;
    setIsLoadingRelationships(true);
    try {
      const response = await fetch(`/api/relationships?form_id=${id}`);
      const data = await response.json();
      
      if (data.success) {
        setRelationships(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load relationships:', error);
    } finally {
      setIsLoadingRelationships(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await fetch('/api/users');
      const data = await response.json();
      
      if (data.success) {
        setUsers(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const loadGroupsPeriods = async () => {
    try {
      const response = await fetch('/api/groups-periods');
      const data = await response.json();
      
      if (data.success) {
        setGroupsPeriods(data.data || []);
      }
    } catch (error) {
      console.error('Failed to load groups_periods:', error);
    }
  };

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateAssignmentDefaults = (patch) => {
    setFormData((prev) => {
      const meta = normalizeFormMetadata(prev.metadata);
      return {
        ...prev,
        metadata: {
          ...meta,
          assignmentDefaults: buildFormAssignmentMetadataForSave({
            ...meta.assignmentDefaults,
            ...patch
          })
        }
      };
    });
  };

  const validateAssignmentDefaults = (metadata) => {
    const defaults = normalizeFormMetadata(metadata).assignmentDefaults;
    const allowResubmission =
      defaults[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;
    const maxAttempts = Number(defaults[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]) || 1;
    if (allowResubmission && maxAttempts < 2) {
      return ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_OVER_ONE;
    }
    if (!allowResubmission && maxAttempts !== 1) {
      return ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_ONE;
    }
    return null;
  };

  const handleAllowResubmissionChange = (checked) => {
    const defaults = normalizeFormMetadata(formData.metadata).assignmentDefaults;
    const currentMax = Number(defaults[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]) || 1;
    if (checked) {
      updateAssignmentDefaults({
        [FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING]: true,
        [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: Math.max(2, currentMax)
      });
      return;
    }
    if (currentMax !== 1) {
      showAssignmentCornerNotice(ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_ONE);
    }
    updateAssignmentDefaults({
      [FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING]: false,
      [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: 1
    });
  };

  const handleMaxAttemptsChange = (rawValue) => {
    const defaults = normalizeFormMetadata(formData.metadata).assignmentDefaults;
    const allowResubmission =
      defaults[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;
    if (rawValue === '') {
      updateAssignmentDefaults({
        [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: ''
      });
      return;
    }
    const n = Number(rawValue);
    if (!Number.isFinite(n)) return;
    const maxAttempts = Math.floor(n);
    if (!allowResubmission && maxAttempts !== 1) {
      showAssignmentCornerNotice(ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_ONE);
      return;
    }
    if (allowResubmission && maxAttempts < 2) {
      showAssignmentCornerNotice(ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_OVER_ONE);
      return;
    }
    updateAssignmentDefaults({
      [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: maxAttempts
    });
  };

  const handleMaxAttemptsBlur = () => {
    const defaults = normalizeFormMetadata(formData.metadata).assignmentDefaults;
    const allowResubmission =
      defaults[FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING] === true;
    if (!allowResubmission) return;
    const n = Number(defaults[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]);
    if (!Number.isFinite(n) || n < 2) {
      updateAssignmentDefaults({
        [FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]: 2
      });
      if (Number.isFinite(n) && n < 2) {
        showAssignmentCornerNotice(ASSIGNMENT_MAX_ATTEMPTS_MSG.MUST_BE_OVER_ONE);
      }
    }
  };

  const openAddRelationshipModal = () => {
    const defaults = normalizeFormMetadata(formData.metadata).assignmentDefaults;
    setNewRelationship({
      parent_table: 'users',
      parent_id: '',
      relationship_type: FORM_RELATIONSHIP_TYPES.FILL_OUT,
      allowViewAnswersAfterSubmission:
        defaults[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION],
      allowSeeWrongAnswersAfterSubmission:
        defaults[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION],
      allowViewPointsEarnedAfterSubmission:
        defaults[FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION],
      assigneePublicRead: defaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ],
      assigneePublicWrite: defaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE],
      assigneeFlagsRead: defaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ],
      assigneeMarkAddressed: defaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]
    });
    setShowAddRelationshipModal(true);
  };

  const groupingOn = isGroupingEnabled(formData.metadata, formData.questions);
  const questionGroups = useMemo(
    () => deriveQuestionGroups(formData.questions, formData.metadata),
    [formData.questions, formData.metadata]
  );

  useEffect(() => {
    if (!groupingOn) return;
    if (!activeGroupTab || !questionGroups.includes(activeGroupTab)) {
      setActiveGroupTab(questionGroups[0] || null);
    }
  }, [groupingOn, questionGroups, activeGroupTab]);

  const addQuestion = (groupName = null) => {
    const base = {
      question_id: generateUUID(),
      type: 'varchar',
      label: '',
      required: true,
      ...(groupingOn && groupName ? { group: groupName } : {})
    };
    const newQuestion = isPointsForm(formData.type)
      ? {
          ...base,
          points: 1,
          awardAttemptPoints: false,
          attemptPoints: 0,
          scoring: defaultScoringForQuestion(base)
        }
      : base;

    setFormData((prev) => ({
      ...prev,
      questions: [...prev.questions, newQuestion]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const prevQ = newQuestions[index];
      newQuestions[index] = {
        ...prevQ,
        [field]: value
      };

      if (field === 'type') {
        const updated = { ...newQuestions[index] };
        if (!CHOICE_QUESTION_TYPES.has(value)) {
          delete updated.options;
        } else if (!updated.options) {
          updated.options = [''];
        }
        if (UPLOAD_QUESTION_TYPES.has(value)) {
          updated.allowMultiple = updated.allowMultiple !== false;
        } else {
          delete updated.allowMultiple;
        }
        if (isPointsForm(prev.type)) {
          updated.scoring = defaultScoringForQuestion({ ...updated, type: value });
          if (updated.points == null) {
            updated.points = 1;
          }
        }
        newQuestions[index] = updated;
      }

      return { ...prev, questions: newQuestions };
    });
  };

  const updateQuestionScoring = (index, patch) => {
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const q = newQuestions[index];
      newQuestions[index] = {
        ...q,
        scoring: {
          ...defaultScoringForQuestion(q),
          ...(q.scoring || {}),
          ...patch
        }
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const toggleCorrectOption = (questionIndex, optionValue) => {
    if (!optionValue?.trim()) return;
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      const q = newQuestions[questionIndex];
      const scoring = {
        ...defaultScoringForQuestion(q),
        ...(q.scoring || {})
      };
      const current = new Set((scoring.correctOptions || []).map(String));
      const val = String(optionValue);
      if (current.has(val)) current.delete(val);
      else current.add(val);
      newQuestions[questionIndex] = {
        ...q,
        scoring: { ...scoring, correctOptions: [...current] }
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const deleteQuestion = (index) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.filter((_, i) => i !== index)
    }));
  };

  const moveQuestion = (index, direction) => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === formData.questions.length - 1)
    ) {
      return;
    }
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    setFormData(prev => {
      const newQuestions = [...prev.questions];
      [newQuestions[index], newQuestions[newIndex]] = [newQuestions[newIndex], newQuestions[index]];
      return { ...prev, questions: newQuestions };
    });
  };

  const addOption = (questionIndex) => {
    setFormData((prev) => {
      const q = prev.questions[questionIndex];
      const options = [...(q.options || []), ''];
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = { ...q, options };
      return { ...prev, questions: newQuestions };
    });
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    setFormData((prev) => {
      const q = prev.questions[questionIndex];
      const prevValue = q.options?.[optionIndex];
      const options = [...(q.options || [])];
      options[optionIndex] = value;
      let scoring = q.scoring;
      if (isPointsForm(prev.type) && q.scoring?.correctOptions?.length && prevValue != null) {
        const correct = q.scoring.correctOptions.map((opt) =>
          String(opt) === String(prevValue) ? value : opt
        );
        scoring = { ...q.scoring, correctOptions: correct.filter((opt) => String(opt).trim()) };
      }
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = { ...q, options, ...(scoring !== q.scoring ? { scoring } : {}) };
      return { ...prev, questions: newQuestions };
    });
  };

  const deleteOption = (questionIndex, optionIndex) => {
    setFormData((prev) => {
      const q = prev.questions[questionIndex];
      const newQuestions = [...prev.questions];
      newQuestions[questionIndex] = {
        ...q,
        options: (q.options || []).filter((_, i) => i !== optionIndex)
      };
      return { ...prev, questions: newQuestions };
    });
  };

  const setGroupingEnabled = (enabled) => {
    setFormData((prev) => {
      const groups = deriveQuestionGroups(prev.questions, prev.metadata);
      let questions = prev.questions;
      if (enabled) {
        const targetGroup = activeGroupTab || groups[0] || DEFAULT_QUESTION_GROUP;
        const nextGroups = groups.length ? groups : [targetGroup];
        questions = prev.questions.map((q) =>
          q.group ? q : { ...q, group: targetGroup }
        );
        setActiveGroupTab(targetGroup);
        return {
          ...prev,
          questions,
          metadata: {
            ...prev.metadata,
            groupingEnabled: true,
            questionGroups: nextGroups
          }
        };
      }
      return {
        ...prev,
        metadata: {
          ...prev.metadata,
          groupingEnabled: false,
          questionGroups: groups
        }
      };
    });
    if (!enabled) {
      setEditingGroupTab(null);
      setShowNewGroupInput(false);
    }
  };

  const addQuestionGroup = (name) => {
    const trimmed = name.trim();
    if (!trimmed) return false;
    const groups = deriveQuestionGroups(formData.questions, formData.metadata);
    if (groups.includes(trimmed)) return false;
    setFormData((prev) => ({
      ...prev,
      metadata: {
        ...prev.metadata,
        groupingEnabled: true,
        questionGroups: [...groups, trimmed]
      }
    }));
    setActiveGroupTab(trimmed);
    return true;
  };

  const renameQuestionGroup = (oldName, newName) => {
    const trimmed = newName.trim();
    if (!trimmed || trimmed === oldName) return;
    const groups = deriveQuestionGroups(formData.questions, formData.metadata);
    if (groups.includes(trimmed) && trimmed !== oldName) {
      showFormAlert('Group exists', 'Choose a different group name.', 'warning');
      return;
    }
    setFormData((prev) => ({
      ...prev,
      questions: prev.questions.map((q) =>
        q.group === oldName ? { ...q, group: trimmed } : q
      ),
      metadata: {
        ...prev.metadata,
        questionGroups: groups.map((g) => (g === oldName ? trimmed : g))
      }
    }));
    if (activeGroupTab === oldName) setActiveGroupTab(trimmed);
  };

  const commitGroupTabEdit = () => {
    if (editingGroupTab) {
      renameQuestionGroup(editingGroupTab, editGroupTabValue);
    }
    setEditingGroupTab(null);
    setEditGroupTabValue('');
  };

  const confirmNewGroupTab = () => {
    if (addQuestionGroup(newGroupTabValue)) {
      setNewGroupTabValue('');
      setShowNewGroupInput(false);
    }
  };

  const openNewGroupInput = () => {
    setShowNewGroupInput(true);
    setNewGroupTabValue('');
    if (!groupingOn) setGroupingEnabled(true);
  };

  const validateForm = () => {
    if (!formData.title.trim()) {
      return 'Form title is required';
    }
    
    if (formData.questions.length === 0) {
      return 'Form must have at least one question';
    }
    
    for (let i = 0; i < formData.questions.length; i++) {
      const q = formData.questions[i];
      if (richTextIsEmpty(q.label)) {
        return `Question ${i + 1} must have a label`;
      }
      if (!q.type) {
        return `Question ${i + 1} must have a type`;
      }
      if ((q.type === 'select' || q.type === 'single-choice' || q.type === 'multiple-choice') &&
          (!q.options || q.options.length === 0 || q.options.some(opt => !opt.trim()))) {
        return `Question "${stripHtmlToText(q.label) || `Q${i + 1}`}" must have at least one option`;
      }
      if (isPointsForm(formData.type)) {
        const scoringError = validateQuestionScoring(q, formData.type);
        if (scoringError) return scoringError;
      }
    }
    
    return null;
  };

  const handleSave = async () => {
    const assignmentDefaultsError = validateAssignmentDefaults(formData.metadata);
    if (assignmentDefaultsError) {
      showAssignmentCornerNotice(assignmentDefaultsError);
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      showFormAlert('Check form', validationError, 'warning');
      return;
    }

    // Check for submissions before updating
    if (isEditing) {
      // Always check submissions right before save (in case they were added since load)
      const submissionCheck = await checkFormSubmissions();
      
      if (submissionCheck.hasSubmissions) {
        showFormAlert(
          'Cannot update',
          `This form has ${submissionCheck.count} submission${submissionCheck.count !== 1 ? 's' : ''} and cannot be edited.`,
          'warning',
          'Create a new form copy when versioning is available.'
        );
        setIsSaving(false);
        return;
      }
    }

    if (isEditing && !formsCaps.canEdit) {
      showFormAlert('Not allowed', 'You do not have permission to edit forms.', 'warning');
      return;
    }
    if (!isEditing && !formsCaps.canCreate) {
      showFormAlert('Not allowed', 'You do not have permission to create forms.', 'warning');
      return;
    }

    setIsSaving(true);
    try {
      const url = isEditing ? `/api/forms/${id}` : '/api/forms';
      const method = isEditing ? 'PUT' : 'POST';
      
      const response = await authFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: formData.title,
          type: formData.type,
          description: formData.description || null,
          primary_taxonomy_id: formData.primary_taxonomy_id || null,
          questions: formData.questions.map((q) => compactQuestionForStorage(q, formData.type)),
          metadata: {
            ...formData.metadata,
            questionGroups: deriveQuestionGroups(formData.questions, formData.metadata)
          }
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        onNotice?.({
          title: isEditing ? 'Saved' : 'Created',
          text: `"${formData.title}" ${isEditing ? 'was updated' : 'was created'}.`
        });

        if (!isEditing) {
          onSaved?.(data.data.id, formData.title);
        } else {
          await loadRelationships();
        }
      } else {
        throw new Error(data.message || 'Save failed');
      }
    } catch (error) {
      showFormAlert(
        'Save failed',
        `Failed to ${isEditing ? 'update' : 'create'} form`,
        'error',
        error.message
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddRelationship = async () => {
    if (!newRelationship.parent_id || !id) {
      showFormAlert('Select target', 'Choose a user or group-period to assign this form.', 'warning');
      return;
    }

    try {
      const isAssignment =
        normalizeFormRelationshipType(newRelationship.relationship_type) ===
        FORM_RELATIONSHIP_TYPES.ASSIGNMENT;
      const metadata = isAssignment
        ? buildFormAssignmentMetadataForSave({
            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION]:
              newRelationship.allowViewAnswersAfterSubmission,
            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION]:
              newRelationship.allowSeeWrongAnswersAfterSubmission,
            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION]:
              newRelationship.allowViewPointsEarnedAfterSubmission,
            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ]:
              newRelationship.assigneePublicRead,
            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE]:
              newRelationship.assigneePublicWrite,
            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ]:
              newRelationship.assigneeFlagsRead,
            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]:
              newRelationship.assigneeMarkAddressed
          })
        : undefined;

      const response = await fetch('/api/relationships', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          parent_table: newRelationship.parent_table,
          parent_id: newRelationship.parent_id,
          child_table: 'forms',
          child_id: id,
          relationship_type: newRelationship.relationship_type || FORM_RELATIONSHIP_TYPES.FILL_OUT,
          ...(metadata ? { metadata } : {})
        })
      });

      const data = await response.json();
      
      if (data.success) {
        onNotice?.({ title: 'Assigned', text: 'Form assignment added.' });
        setShowAddRelationshipModal(false);
        setNewRelationship({
          parent_table: 'users',
          parent_id: '',
          relationship_type: FORM_RELATIONSHIP_TYPES.FILL_OUT,
          allowViewAnswersAfterSubmission: false,
          allowSeeWrongAnswersAfterSubmission: false,
          allowViewPointsEarnedAfterSubmission: true,
          assigneePublicRead: true,
          assigneePublicWrite: true,
          assigneeFlagsRead: true,
          assigneeMarkAddressed: true
        });
        await loadRelationships();
      } else {
        throw new Error(data.message || 'Failed to create relationship');
      }
    } catch (error) {
      showFormAlert('Assign failed', 'Could not create assignment.', 'error', error.message);
    }
  };

  const handleDeleteRelationship = async (relationshipId) => {
    try {
      const response = await fetch(`/api/relationships/${relationshipId}`, {
        method: 'DELETE'
      });

      const data = await response.json();
      
      if (data.success) {
        onNotice?.({ title: 'Removed', text: 'Assignment removed.' });
        setRemoveRelConfirm(null);
        await loadRelationships();
      } else {
        throw new Error(data.message || 'Failed to delete relationship');
      }
    } catch (error) {
      showFormAlert('Remove failed', 'Could not remove assignment.', 'error', error.message);
    }
  };

  const confirmDeleteQuestion = () => {
    if (deleteQuestionIndex === null) return;
    deleteQuestion(deleteQuestionIndex);
    setDeleteQuestionIndex(null);
  };

  const visibleQuestions = useMemo(() => {
    const indexed = formData.questions.map((question, index) => ({ ...question, index }));
    if (!groupingOn) return indexed;
    const tab = activeGroupTab || questionGroups[0];
    return indexed.filter((q) => (q.group || questionGroups[0]) === tab);
  }, [formData.questions, groupingOn, activeGroupTab, questionGroups]);

  const taxonomyScopeAll =
    formsCaps.taxonomyScope === FORMS_TAXONOMY_SCOPES.ALL_ORGANIZATIONS;

  const groupedSubjectTaxonomy = useMemo(() => {
    const selectable = taxonomyOptions.filter((t) => t.selectable !== false);
    const external = taxonomyOptions.filter((t) => t.outside_scope === true);
    return {
      external,
      global: selectable.filter((t) => t.is_global),
      ownOrganization: selectable.filter((t) => t.is_own_organization),
      otherOrganizations: selectable.filter((t) => t.is_other_organization)
    };
  }, [taxonomyOptions]);

  const previewForm = useMemo(
    () => ({
      title: formData.title || 'Untitled form',
      description: formData.description || '',
      type: formData.type,
      questions: formData.questions
    }),
    [formData.title, formData.description, formData.type, formData.questions]
  );

  const previewQuestionsJson = useMemo(
    () =>
      JSON.stringify(
        previewForm.questions.map((q) => compactQuestionForStorage(q, previewForm.type)),
        null,
        2
      ),
    [previewForm.questions, previewForm.type]
  );

  const openPreview = () => {
    if (!formData.questions.length) {
      showFormAlert('Nothing to preview', 'Add at least one question first.', 'warning');
      return;
    }
    setPreviewTab('form');
    setShowPreview(true);
  };

  return (
    <div className="forms-editor forms-editor--focused">
      <LoadingOverlay isVisible={isLoading || isSaving} message={isLoading ? 'Loading form…' : 'Saving…'} />

      <button type="button" onClick={onBack} className="forms-back-link">
        ← All forms
      </button>

      <div className="forms-editor-head">
        <h2 className="forms-editor-heading">
          {isEditing ? formData.title || 'Untitled form' : 'New form'}
        </h2>
        <div className="forms-editor-head-actions">
          <button
            type="button"
            className="forms-editor-help-btn"
            onClick={() => setShowEditorHelp(true)}
            aria-label="Form editor help"
            title="How this editor works"
          >
            <FormsInfoIcon />
          </button>
          {isEditing && formsCaps.canAssign ? (
            <button
              type="button"
              className="forms-editor-assign-btn"
              onClick={() => setShowAssignmentsModal(true)}
              title={`Assignments${relationships.length ? ` (${relationships.length})` : ''}`}
              aria-label="Assignments"
            >
              <FormsLinkIcon />
            </button>
          ) : null}
          {isEditing && hasSubmissions ? (
            <span className="forms-editor-badge">
              {submissionCount} submission{submissionCount !== 1 ? 's' : ''} · locked
            </span>
          ) : null}
        </div>
      </div>

      <div className="forms-editor-body">
        {/* Setup */}
        <section className="forms-panel">
          <h3 className="forms-panel-title">Setup</h3>
          <div className="forms-field">
            <label className="forms-field-label" htmlFor="form-editor-title">Title *</label>
            <input
              id="form-editor-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleFormChange('title', e.target.value)}
              placeholder="e.g., Week 3 math assignment"
              className="forms-field-input"
            />
          </div>
          <div className="forms-field-row">
            <div className="forms-field">
              <span className="forms-field-label" id="form-editor-type-label">Form type</span>
              <div
                className="forms-type-radios"
                role="radiogroup"
                aria-labelledby="form-editor-type-label"
              >
                {[FORM_TYPES.SURVEY, FORM_TYPES.POINTS].map((typeValue) => (
                  <label
                    key={typeValue}
                    className={`forms-type-radio${formData.type === typeValue ? ' forms-type-radio--active' : ''}`}
                  >
                    <input
                      type="radio"
                      name="form-editor-type"
                      value={typeValue}
                      checked={formData.type === typeValue}
                      onChange={(e) => {
                        const nextType = e.target.value;
                        setFormData((prev) => {
                          let questions = prev.questions;
                          if (isPointsForm(nextType) && !isPointsForm(prev.type)) {
                            questions = prev.questions.map((q) => ({
                              ...q,
                              points: q.points != null ? q.points : 1,
                              awardAttemptPoints: q.awardAttemptPoints === true,
                              attemptPoints: q.attemptPoints != null ? q.attemptPoints : 0,
                              scoring: q.scoring || defaultScoringForQuestion(q)
                            }));
                          }
                          return { ...prev, type: nextType, questions };
                        });
                      }}
                    />
                    <span>{FORM_TYPE_LABELS[typeValue]}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="forms-field">
              <span className="forms-field-label" id="form-editor-subject-label">Subject</span>
              <button
                type="button"
                id="form-editor-subject"
                className={`forms-subject-badge${selectedTaxonomy ? '' : ' forms-subject-badge--empty'}`}
                onClick={openSubjectPicker}
                aria-labelledby="form-editor-subject-label"
              >
                <span className="forms-subject-badge-label">
                  {selectedTaxonomy?.title || 'Select subject'}
                </span>
              </button>
            </div>
          </div>
          <div className="forms-field">
            <label className="forms-field-label" htmlFor="form-editor-description">Description</label>
            <textarea
              id="form-editor-description"
              value={formData.description}
              onChange={(e) => handleFormChange('description', e.target.value)}
              placeholder="Instructions for students or internal notes"
              className="forms-field-input"
              rows={3}
            />
          </div>
          <div className="forms-setup-defaults">
            <div className="forms-setup-defaults-head">
              <span className="forms-setup-defaults-title">Default submission settings</span>
              <span className="forms-setup-defaults-hint">
                Per-assignment overrides may replace these defaults.
              </span>
            </div>
            <div className="forms-setup-defaults-opts">
              {(() => {
                const assignmentDefaults = normalizeFormMetadata(formData.metadata).assignmentDefaults;
                const allowResubmission =
                  assignmentDefaults[
                    FORM_ASSIGNMENT_METADATA_KEYS.RESUBMISSION_ALLOWED_AFTER_GRADING
                  ] === true;
                return (
                  <>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={allowResubmission}
                        onChange={(e) => handleAllowResubmissionChange(e.target.checked)}
                      />
                      Allow resubmission
                    </label>
                    {allowResubmission ? (
                      <label className="forms-setup-defaults-check forms-setup-defaults-check--attempts forms-setup-defaults-check--attempts-nested">
                        <input
                          type="number"
                          min="2"
                          step="1"
                          className="forms-setup-defaults-attempts-input"
                          aria-label="Max attempts"
                          value={
                            assignmentDefaults[FORM_ASSIGNMENT_METADATA_KEYS.MAX_ATTEMPTS]
                          }
                          onChange={(e) => handleMaxAttemptsChange(e.target.value)}
                          onBlur={handleMaxAttemptsBlur}
                        />
                        Max attempts
                      </label>
                    ) : null}
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[
                            FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION
                          ]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_ANSWERS_AFTER_SUBMISSION]:
                              e.target.checked
                          })
                        }
                      />
                      Answer key shown after submit
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[
                            FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION
                          ]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_SEE_WRONG_ANSWERS_AFTER_SUBMISSION]:
                              e.target.checked
                          })
                        }
                      />
                      Response shown whether incorrect after submit
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[
                            FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION
                          ]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ALLOW_VIEW_POINTS_EARNED_AFTER_SUBMISSION]:
                              e.target.checked
                          })
                        }
                      />
                      Points earned per question shown after grading
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_READ]: e.target.checked
                          })
                        }
                      />
                      Assignee may read public chat
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_PUBLIC_WRITE]: e.target.checked
                          })
                        }
                      />
                      Assignee may post in public chat
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_FLAGS_READ]: e.target.checked
                          })
                        }
                      />
                      Assignee may see flags
                    </label>
                    <label className="forms-setup-defaults-check">
                      <input
                        type="checkbox"
                        checked={
                          assignmentDefaults[FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]
                        }
                        onChange={(e) =>
                          updateAssignmentDefaults({
                            [FORM_ASSIGNMENT_METADATA_KEYS.ASSIGNEE_MARK_ADDRESSED]: e.target.checked
                          })
                        }
                      />
                      Assignee may mark flags addressed
                    </label>
                  </>
                );
              })()}
            </div>
          </div>
        </section>

        <section className="forms-panel">
          <div className="forms-panel-head">
            <h3 className="forms-panel-title">Questions</h3>
            <div className="forms-q-toolbar">
              <label className="forms-group-toggle" title="Group questions into tabs">
                <FormsGroupIcon />
                <span className="forms-group-toggle-label">Groups</span>
                <input
                  type="checkbox"
                  className="forms-group-toggle-input"
                  checked={groupingOn}
                  onChange={(e) => setGroupingEnabled(e.target.checked)}
                />
                <span className="forms-group-toggle-track" aria-hidden />
              </label>
            </div>
          </div>

          {groupingOn ? (
            <div className="forms-q-tabs" role="tablist" aria-label="Question groups">
              {questionGroups.map((groupName) => (
                <div key={groupName} className="forms-q-tab-wrap">
                  {editingGroupTab === groupName ? (
                    <input
                      type="text"
                      className="forms-q-tab-input"
                      value={editGroupTabValue}
                      onChange={(e) => setEditGroupTabValue(e.target.value)}
                      onBlur={commitGroupTabEdit}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          commitGroupTabEdit();
                        }
                        if (e.key === 'Escape') {
                          setEditingGroupTab(null);
                          setEditGroupTabValue('');
                        }
                      }}
                      autoFocus
                    />
                  ) : (
                    <button
                      type="button"
                      role="tab"
                      aria-selected={activeGroupTab === groupName}
                      className={`forms-q-tab${activeGroupTab === groupName ? ' forms-q-tab--active' : ''}`}
                      onClick={() => setActiveGroupTab(groupName)}
                      onDoubleClick={() => {
                        setEditingGroupTab(groupName);
                        setEditGroupTabValue(groupName);
                      }}
                      title="Double-click to rename"
                    >
                      {groupName}
                    </button>
                  )}
                </div>
              ))}
              {showNewGroupInput ? (
                <input
                  type="text"
                  className="forms-q-tab-input forms-q-tab-input--new"
                  value={newGroupTabValue}
                  onChange={(e) => setNewGroupTabValue(e.target.value)}
                  placeholder="Group name"
                  onBlur={() => {
                    if (newGroupTabValue.trim()) confirmNewGroupTab();
                    else setShowNewGroupInput(false);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      confirmNewGroupTab();
                    }
                    if (e.key === 'Escape') {
                      setShowNewGroupInput(false);
                      setNewGroupTabValue('');
                    }
                  }}
                  autoFocus
                />
              ) : (
                <button
                  type="button"
                  onClick={openNewGroupInput}
                  className="forms-icon-btn forms-q-tab-add"
                  title="Add group"
                  aria-label="Add group"
                >
                  +
                </button>
              )}
            </div>
          ) : null}

          {formData.questions.length === 0 ? (
            <button
              type="button"
              onClick={() => addQuestion(groupingOn ? activeGroupTab || questionGroups[0] : null)}
              className="forms-q-add"
            >
              + Add your first question
            </button>
          ) : (
            <div className="forms-q-list">
              {visibleQuestions.map(({ index, ...question }) => (
                <QuestionEditor
                  key={question.question_id || index}
                  question={question}
                  index={index}
                  formType={formData.type}
                  groupingOn={groupingOn}
                  groupOptions={questionGroups}
                  onUpdate={(field, value) => updateQuestion(index, field, value)}
                  onUpdateScoring={(patch) => updateQuestionScoring(index, patch)}
                  onToggleCorrectOption={(optValue) => toggleCorrectOption(index, optValue)}
                  onRequestDelete={() => setDeleteQuestionIndex(index)}
                  onMove={(direction) => moveQuestion(index, direction)}
                  onAddOption={() => addOption(index)}
                  onUpdateOption={(optIndex, value) => updateOption(index, optIndex, value)}
                  onDeleteOption={(optIndex) => deleteOption(index, optIndex)}
                  canMoveUp={index > 0}
                  canMoveDown={index < formData.questions.length - 1}
                />
              ))}
              <button
                type="button"
                onClick={() => addQuestion(groupingOn ? activeGroupTab || questionGroups[0] : null)}
                className="forms-q-add"
              >
                + Add question
              </button>
            </div>
          )}
        </section>

        <div className="forms-editor-footer">
          <button type="button" onClick={onBack} className="forms-btn">
            Cancel
          </button>
          <button
            type="button"
            onClick={openPreview}
            className="forms-btn"
            disabled={!formData.questions.length}
          >
            Preview
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={
              isSaving ||
              (isEditing && hasSubmissions) ||
              (isEditing && !formsCaps.canEdit) ||
              (!isEditing && !formsCaps.canCreate)
            }
            className="forms-btn forms-btn--primary"
            title={isEditing && hasSubmissions ? 'This form has submissions and cannot be updated directly' : ''}
          >
            {isSaving ? 'Saving…' : isEditing ? 'Save changes' : 'Create form'}
          </button>
        </div>
      </div>

      {alertModal ? (
        <AlertModal
          isOpen={true}
          onClose={() => setAlertModal(null)}
          showCloseButton={false}
          allowOverlayClose={true}
          {...PROMPT_ALERT}
          variant={alertModal.variant || 'info'}
          subject={alertModal.subject}
          body={alertModal.body}
          details={alertModal.details}
          actions={alertModal.actions}
        />
      ) : null}

      {cornerNotice ? (
        <CornerNotice {...cornerNotice} onClose={() => setCornerNotice(null)} />
      ) : null}

      {removeRelConfirm ? (
        <AlertModal
          isOpen={true}
          onClose={() => setRemoveRelConfirm(null)}
          variant="warning"
          subject="Remove assignment?"
          body="This form will no longer be assigned to that context."
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setRemoveRelConfirm(null), variant: 'secondary' },
            {
              label: 'Remove',
              onClick: () => handleDeleteRelationship(removeRelConfirm),
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          {...PROMPT_ALERT}
        />
      ) : null}

      {deleteQuestionIndex !== null ? (
        <AlertModal
          isOpen={true}
          onClose={() => setDeleteQuestionIndex(null)}
          variant="warning"
          subject="Delete question?"
          body="This question will be removed from the form."
          showCloseButton={false}
          allowOverlayClose={true}
          actions={[
            { label: 'Cancel', onClick: () => setDeleteQuestionIndex(null), variant: 'secondary' },
            {
              label: 'Delete',
              onClick: confirmDeleteQuestion,
              variant: 'primary',
              style: { background: '#dc2626' }
            }
          ]}
          {...PROMPT_ALERT}
        />
      ) : null}

      {showPreview ? (
        <FormEditorPreview
          form={previewForm}
          previewTab={previewTab}
          onPreviewTabChange={setPreviewTab}
          questionsJson={previewQuestionsJson}
          onClose={() => setShowPreview(false)}
          onPreviewSubmit={(data) => {
            console.log('Preview submission (not saved):', data);
            showFormAlert(
              'Preview only',
              'In preview mode, responses are not saved.',
              'info'
            );
          }}
        />
      ) : null}

      {showSubjectPicker ? (
        <div
          className="forms-help-overlay"
          role="presentation"
          onClick={() => setShowSubjectPicker(false)}
        >
          <div
            className="forms-help-dialog forms-subject-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby="form-subject-picker-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-help-header">
              <h3 id="form-subject-picker-title" className="forms-help-title">Select subject</h3>
              <button
                type="button"
                className="forms-sheet-close"
                onClick={() => setShowSubjectPicker(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="forms-help-body forms-subject-picker-body">
              {taxonomyLoading ? (
                <p className="forms-panel-loading">Loading subjects…</p>
              ) : (
                <>
                  {!taxonomyScopeAll ? (
                    <p className="forms-subject-picker-intro">
                      Showing global subjects and your organization&apos;s taxonomy.
                    </p>
                  ) : (
                    <p className="forms-subject-picker-intro">
                      Showing subjects from all organizations.
                    </p>
                  )}

                  {groupedSubjectTaxonomy.external.length ? (
                    <section className="forms-subject-picker-section">
                      <h4 className="forms-subject-picker-section-title">Current subject</h4>
                      {groupedSubjectTaxonomy.external.map((item) => (
                        <div key={item.id} className="forms-subject-current">
                          <div className="forms-subject-option">
                            <div className="forms-subject-option-main">
                              <span>{item.title || item.short_title}</span>
                              {item.organization_group_name ? (
                                <span className="forms-subject-option-org">{item.organization_group_name}</span>
                              ) : null}
                            </div>
                          </div>
                          <p className="forms-subject-picker-note">
                            This subject is from another organization
                            {item.organization_group_name ? ` (${item.organization_group_name})` : ''}.
                            You can keep it, clear it, or choose from your organization&apos;s taxonomy.
                          </p>
                        </div>
                      ))}
                    </section>
                  ) : null}

                  {groupedSubjectTaxonomy.global.length ? (
                    <section className="forms-subject-picker-section">
                      <h4 className="forms-subject-picker-section-title">Global</h4>
                      <div className="forms-subject-option-list">
                        {groupedSubjectTaxonomy.global.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className={`forms-subject-option forms-subject-option--pickable${String(formData.primary_taxonomy_id) === String(item.id) ? ' forms-subject-option--active' : ''}`}
                            onClick={() => selectSubjectTaxonomy(item)}
                          >
                            <span>{item.title || item.short_title}</span>
                            <span className="forms-subject-option-org">Global</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {groupedSubjectTaxonomy.ownOrganization.length ? (
                    <section className="forms-subject-picker-section">
                      <h4 className="forms-subject-picker-section-title">Your organization</h4>
                      <div className="forms-subject-option-list">
                        {groupedSubjectTaxonomy.ownOrganization.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className={`forms-subject-option forms-subject-option--pickable${String(formData.primary_taxonomy_id) === String(item.id) ? ' forms-subject-option--active' : ''}`}
                            onClick={() => selectSubjectTaxonomy(item)}
                          >
                            <span>{item.title || item.short_title}</span>
                          </button>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {taxonomyScopeAll && groupedSubjectTaxonomy.otherOrganizations.length ? (
                    <section className="forms-subject-picker-section">
                      <h4 className="forms-subject-picker-section-title">Other organizations</h4>
                      <div className="forms-subject-option-list">
                        {groupedSubjectTaxonomy.otherOrganizations.map((item) => (
                          <button
                            type="button"
                            key={item.id}
                            className={`forms-subject-option forms-subject-option--pickable${String(formData.primary_taxonomy_id) === String(item.id) ? ' forms-subject-option--active' : ''}`}
                            onClick={() => selectSubjectTaxonomy(item)}
                          >
                            <span>{item.title || item.short_title}</span>
                            {item.organization_group_name ? (
                              <span className="forms-subject-option-org">{item.organization_group_name}</span>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    </section>
                  ) : null}

                  {!taxonomyLoading &&
                  !groupedSubjectTaxonomy.global.length &&
                  !groupedSubjectTaxonomy.ownOrganization.length &&
                  !groupedSubjectTaxonomy.otherOrganizations.length &&
                  !groupedSubjectTaxonomy.external.length ? (
                    <p className="forms-panel-empty">No subjects available.</p>
                  ) : null}
                </>
              )}
            </div>
            <div className="forms-subject-picker-footer">
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--secondary"
                onClick={clearSubjectTaxonomy}
                disabled={!formData.primary_taxonomy_id}
              >
                Clear subject
              </button>
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--primary"
                onClick={() => setShowSubjectPicker(false)}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showEditorHelp ? (
        <div
          className="forms-help-overlay"
          role="presentation"
          onClick={() => setShowEditorHelp(false)}
        >
          <div
            className="forms-help-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="form-editor-help-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-help-header">
              <h3 id="form-editor-help-title" className="forms-help-title">Form editor guide</h3>
              <button
                type="button"
                className="forms-sheet-close"
                onClick={() => setShowEditorHelp(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="forms-help-body">
              <section className="forms-help-section">
                <h4 className="forms-help-section-title">Form type</h4>
                <p>
                  Choose <strong>{FORM_TYPE_LABELS[FORM_TYPES.SURVEY]}</strong> to collect responses without scoring.
                  Choose <strong>{FORM_TYPE_LABELS[FORM_TYPES.POINTS]}</strong> to award points per question using answer rules below.
                </p>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">
                  <FormsGroupIcon />
                  Groups
                </h4>
                <p>
                  In the Questions panel, turn on <strong>Groups</strong> to organize questions into tabs.
                  Use <strong>+</strong> next to the tabs to add a group. Double-click a tab name to rename it.
                  When grouping is off, all questions appear in one flat list.
                </p>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">Answer scoring</h4>
                <p>On points forms, each question has an answer scoring mode (shown as radio buttons):</p>
                <ul className="forms-help-list">
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.FREE]}</strong> — any non-empty answer earns full points.</li>
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.CHOICES]}</strong> — mark correct option(s) on choice fields.</li>
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.TEXT]}</strong> — match text (includes or equals exactly).</li>
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.NUMBER_EXACT]}</strong> / <strong>{ANSWER_MODE_LABELS[ANSWER_MODES.NUMBER_RANGE]}</strong> — for number fields.</li>
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.DATE_EXACT]}</strong> / <strong>{ANSWER_MODE_LABELS[ANSWER_MODES.DATE_RANGE]}</strong> — for date fields.</li>
                  <li><strong>{ANSWER_MODE_LABELS[ANSWER_MODES.MANUAL]}</strong> — grader awards points after review.</li>
                </ul>
                <p>Available modes depend on the question type. Multiple choice requires selecting exactly the correct set.</p>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">Question types</h4>
                <p>
                  <strong>File upload</strong> lets respondents attach one or more files.
                  Use <strong>Free</strong> to award points when any file is uploaded, or <strong>Manual</strong> for grader review.
                  Enable <strong>Attempt</strong> to award minimum points (e.g. on upload while awaiting review, or when no file is attached on optional questions).
                </p>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">Points badges</h4>
                <p>
                  <span className="forms-q-points-badge forms-q-points-badge--correct forms-q-points-badge--sample">10 pt</span>
                  {' '}Green badge — points for a <strong>correct</strong> answer. Double-click to edit.
                </p>
                <p>
                  <button type="button" className="forms-q-attempt-toggle forms-q-attempt-toggle--on forms-help-inline-btn" tabIndex={-1}>Attempt</button>
                  {' '}Toggle to award minimum points for wrong answers. A{' '}
                  <span className="forms-q-points-badge forms-q-points-badge--attempt forms-q-points-badge--sample">2 pt</span>
                  {' '}red badge appears — double-click to set the amount. Empty answers earn no attempt points.
                </p>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">Default submission settings</h4>
                <p>
                  In <strong>Setup</strong>, configure defaults for assignment submissions. Individual assignments can override these in the assignments panel.
                </p>
                <ul className="forms-help-list">
                  <li><strong>Allow resubmission</strong> — enables multiple attempts and resubmission after grading. When off, students get a single attempt (max attempts = 1).</li>
                  <li><strong>Max attempts</strong> — shown when Allow resubmission is on; must be greater than 1. Limits how many submission rows a student may create.</li>
                  <li><strong>Answer key shown after submit</strong> — submitter can view the expected answers after turning in.</li>
                  <li><strong>Response shown whether incorrect after submit</strong> — submitter can see which responses were incorrect.</li>
                </ul>
              </section>

              <section className="forms-help-section">
                <h4 className="forms-help-section-title">
                  <FormsLinkIcon />
                  Assignments
                </h4>
                <p>
                  After saving, use the <strong>chain icon</strong> next to this help button to link the form to people or groups.
                  Assignments control who should fill out the form.
                </p>
                <ul className="forms-help-list">
                  <li><strong>{FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.FILL_OUT]}</strong> — {FORM_RELATIONSHIP_TYPE_HINTS[FORM_RELATIONSHIP_TYPES.FILL_OUT]}</li>
                  <li><strong>{FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.ASSIGNMENT]}</strong> — {FORM_RELATIONSHIP_TYPE_HINTS[FORM_RELATIONSHIP_TYPES.ASSIGNMENT]}</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      ) : null}

      {showAssignmentsModal && isEditing ? (
        <div
          className="forms-sheet-overlay"
          role="presentation"
          onClick={() => setShowAssignmentsModal(false)}
        >
          <div
            className="forms-sheet-panel forms-sheet-panel--wide"
            role="dialog"
            aria-modal="true"
            aria-labelledby="form-assignments-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-sheet-header">
              <h3 id="form-assignments-title" className="forms-sheet-title">Assignments</h3>
              <div className="forms-sheet-header-actions">
                <button
                  type="button"
                  onClick={openAddRelationshipModal}
                  className="forms-link-btn"
                >
                  + Assign
                </button>
                <button
                  type="button"
                  className="forms-sheet-close"
                  onClick={() => setShowAssignmentsModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </div>
            <div className="forms-sheet-body">
              {isLoadingRelationships ? (
                <p className="forms-panel-loading">Loading assignments…</p>
              ) : relationships.length === 0 ? (
                <p className="forms-panel-empty">No assignments yet. Use + Assign to link this form.</p>
              ) : (
                <div className="forms-rel-list">
                  {relationships.map((rel) => (
                    <div key={rel.id} className="forms-rel-row">
                      <div className="forms-rel-main">
                        <span className="forms-rel-kind">
                          {rel.parent_table === 'users' ? 'User' : 'Group-period'}
                        </span>
                        <span className="forms-rel-name">{rel.parent_name || rel.parent_id}</span>
                        <span className="forms-rel-type">
                          {formatFormRelationshipTypeLabel(rel.relationship_type)}
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={() => setRemoveRelConfirm(rel.id)}
                        className="forms-btn forms-btn--danger forms-btn--compact"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      {showAddRelationshipModal ? (
        <div className="forms-sheet-overlay" role="presentation" onClick={() => setShowAddRelationshipModal(false)}>
          <div
            className="forms-sheet-panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="form-add-assignment-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-sheet-header">
              <h3 id="form-add-assignment-title" className="forms-sheet-title">Assign form</h3>
              <button
                type="button"
                className="forms-sheet-close"
                onClick={() => setShowAddRelationshipModal(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="forms-sheet-body">
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="rel-parent-table">Assign to</label>
                <select
                  id="rel-parent-table"
                  value={newRelationship.parent_table}
                  onChange={(e) => setNewRelationship(prev => ({
                    ...prev,
                    parent_table: e.target.value,
                    parent_id: ''
                  }))}
                  className="forms-field-input"
                >
                  <option value="users">User</option>
                  <option value="groups_periods">Group-period</option>
                </select>
              </div>
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="rel-parent-id">
                  {newRelationship.parent_table === 'users' ? 'User' : 'Group-period'} *
                </label>
                <select
                  id="rel-parent-id"
                  value={newRelationship.parent_id}
                  onChange={(e) => setNewRelationship(prev => ({
                    ...prev,
                    parent_id: e.target.value
                  }))}
                  className="forms-field-input"
                >
                  <option value="">Select…</option>
                  {newRelationship.parent_table === 'users' ? (
                    users.map(user => (
                      <option key={user.id} value={user.id}>
                        {user.first_name} {user.last_name} ({user.email})
                      </option>
                    ))
                  ) : (
                    groupsPeriods.map(gp => (
                      <option key={gp.id} value={gp.id}>
                        {gp.title || (gp.group_name && gp.period_title
                          ? `${gp.group_name} — ${gp.period_title}`
                          : `Group-period ${gp.id}`)}
                      </option>
                    ))
                  )}
                </select>
              </div>
              <div className="forms-field">
                <label className="forms-field-label" htmlFor="rel-type">Behavior</label>
                <select
                  id="rel-type"
                  value={newRelationship.relationship_type}
                  onChange={(e) => setNewRelationship(prev => ({
                    ...prev,
                    relationship_type: e.target.value
                  }))}
                  className="forms-field-input"
                >
                  <option value={FORM_RELATIONSHIP_TYPES.FILL_OUT}>
                    {FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.FILL_OUT]}
                  </option>
                  <option value={FORM_RELATIONSHIP_TYPES.ASSIGNMENT}>
                    {FORM_RELATIONSHIP_TYPE_LABELS[FORM_RELATIONSHIP_TYPES.ASSIGNMENT]}
                  </option>
                </select>
                <p className="forms-field-hint">
                  {FORM_RELATIONSHIP_TYPE_HINTS[newRelationship.relationship_type] || ''}
                </p>
              </div>
              {normalizeFormRelationshipType(newRelationship.relationship_type) ===
              FORM_RELATIONSHIP_TYPES.ASSIGNMENT ? (
                <div className="forms-field forms-field--assignment-meta">
                  <span className="forms-field-label">Assignment overrides</span>
                  <p className="forms-field-hint">
                    Overrides default submission settings in Setup for this assignment.
                  </p>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.allowViewAnswersAfterSubmission}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          allowViewAnswersAfterSubmission: e.target.checked
                        }))
                      }
                    />
                    Allow submitter to view answer key
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.allowSeeWrongAnswersAfterSubmission}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          allowSeeWrongAnswersAfterSubmission: e.target.checked
                        }))
                      }
                    />
                    Allow submitter to see which answers were wrong
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.allowViewPointsEarnedAfterSubmission}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          allowViewPointsEarnedAfterSubmission: e.target.checked
                        }))
                      }
                    />
                    Allow submitter to view points earned per question
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.assigneePublicRead}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          assigneePublicRead: e.target.checked
                        }))
                      }
                    />
                    Assignee may read public chat
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.assigneePublicWrite}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          assigneePublicWrite: e.target.checked
                        }))
                      }
                    />
                    Assignee may post in public chat
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.assigneeFlagsRead}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          assigneeFlagsRead: e.target.checked
                        }))
                      }
                    />
                    Assignee may see flags
                  </label>
                  <label className="forms-q-check forms-q-check--inline">
                    <input
                      type="checkbox"
                      checked={newRelationship.assigneeMarkAddressed}
                      onChange={(e) =>
                        setNewRelationship((prev) => ({
                          ...prev,
                          assigneeMarkAddressed: e.target.checked
                        }))
                      }
                    />
                    Assignee may mark flags addressed
                  </label>
                </div>
              ) : null}
            </div>
            <div className="forms-sheet-actions">
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--secondary"
                onClick={() => setShowAddRelationshipModal(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="forms-sheet-btn forms-sheet-btn--primary"
                onClick={handleAddRelationship}
              >
                Add
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}

const QuestionEditor = ({
  question,
  index,
  formType,
  groupingOn,
  groupOptions,
  onUpdate,
  onUpdateScoring,
  onToggleCorrectOption,
  onRequestDelete,
  onMove,
  onAddOption,
  onUpdateOption,
  onDeleteOption,
  canMoveUp,
  canMoveDown
}) => {
  const [editingPoints, setEditingPoints] = useState(false);
  const [editPointsValue, setEditPointsValue] = useState('');
  const [editingAttemptPoints, setEditingAttemptPoints] = useState(false);
  const [editAttemptPointsValue, setEditAttemptPointsValue] = useState('');
  const [showTypePicker, setShowTypePicker] = useState(false);
  const [labelRichOpen, setLabelRichOpen] = useState(false);

  const labelFieldId = `q-label-${question.question_id || index}`;
  const labelInputId = `q-label-input-${question.question_id || index}`;
  const labelHasFormatting = looksLikeHtml(question.label);

  const startEditPoints = () => {
    setEditPointsValue(String(question.points ?? 1));
    setEditingPoints(true);
  };

  const commitEditPoints = () => {
    onUpdate('points', Math.max(0, Number(editPointsValue) || 0));
    setEditingPoints(false);
  };

  const startEditAttemptPoints = () => {
    setEditAttemptPointsValue(String(question.attemptPoints ?? 1));
    setEditingAttemptPoints(true);
  };

  const commitEditAttemptPoints = () => {
    onUpdate('attemptPoints', Math.max(0, Number(editAttemptPointsValue) || 0));
    setEditingAttemptPoints(false);
  };

  const toggleAttemptPoints = () => {
    const enabling = !question.awardAttemptPoints;
    onUpdate('awardAttemptPoints', enabling);
    if (enabling && !(Number(question.attemptPoints) > 0)) {
      onUpdate('attemptPoints', 1);
    }
  };

  const fieldTypes = QUESTION_TYPE_OPTIONS;
  const selectedType = getQuestionTypeOption(question.type || 'varchar');

  const selectQuestionType = (typeValue) => {
    onUpdate('type', typeValue);
    setShowTypePicker(false);
  };

  const needsOptions = CHOICE_QUESTION_TYPES.has(question.type);
  const pointsMode = isPointsForm(formType);
  const allowedAnswerModes = getAnswerModesForQuestionType(question.type || 'varchar');
  const defaults = defaultScoringForQuestion(question);
  const stored = question.scoring && typeof question.scoring === 'object' ? question.scoring : {};
  const answerMode = allowedAnswerModes.includes(stored.answerMode)
    ? stored.answerMode
    : defaults.answerMode;
  const scoring = { ...defaults, ...stored, answerMode };
  const textRule = scoring.textRule || { match: TEXT_MATCH_TYPES.INCLUDES, value: '', caseSensitive: false };
  const numberRule = { ...defaultNumberRule(), ...(scoring.numberRule || {}) };
  const dateRule = { ...defaultDateRule(), ...(scoring.dateRule || {}) };
  const showChoiceScoring = pointsMode && answerMode === ANSWER_MODES.CHOICES && needsOptions;
  const showTextScoring = pointsMode && answerMode === ANSWER_MODES.TEXT && TEXT_QUESTION_TYPES.has(question.type);
  const showNumberExactScoring = pointsMode && answerMode === ANSWER_MODES.NUMBER_EXACT && NUMBER_QUESTION_TYPES.has(question.type);
  const showNumberRangeScoring = pointsMode && answerMode === ANSWER_MODES.NUMBER_RANGE && NUMBER_QUESTION_TYPES.has(question.type);
  const showDateExactScoring = pointsMode && answerMode === ANSWER_MODES.DATE_EXACT && DATE_QUESTION_TYPES.has(question.type);
  const showDateRangeScoring = pointsMode && answerMode === ANSWER_MODES.DATE_RANGE && DATE_QUESTION_TYPES.has(question.type);
  const showAnswerModeSelect = pointsMode && allowedAnswerModes.length > 1;
  const showAttemptControls =
    pointsMode &&
    (UPLOAD_QUESTION_TYPES.has(question.type)
      ? answerMode === ANSWER_MODES.FREE || answerMode === ANSWER_MODES.MANUAL
      : answerMode !== ANSWER_MODES.FREE && answerMode !== ANSWER_MODES.MANUAL);

  return (
    <div className="forms-q-card">
      <div className="forms-q-card-head">
        <div className="forms-q-card-head-main">
          <span className="forms-q-card-label">Q{index + 1}</span>
          <span className="forms-q-required-label">Required</span>
          {pointsMode ? (
            <div className="forms-q-points-row">
              {editingPoints ? (
                <input
                  type="number"
                  min="0"
                  step="1"
                  className="forms-q-points-input forms-q-points-input--correct"
                  value={editPointsValue}
                  onChange={(e) => setEditPointsValue(e.target.value)}
                  onBlur={commitEditPoints}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitEditPoints();
                    }
                    if (e.key === 'Escape') {
                      setEditingPoints(false);
                    }
                  }}
                  aria-label="Correct answer points"
                  autoFocus
                />
              ) : (
                <span
                  className="forms-q-points-badge forms-q-points-badge--correct"
                  onDoubleClick={startEditPoints}
                  title="Double-click to edit correct-answer points"
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      startEditPoints();
                    }
                  }}
                >
                  {Number(question.points) || 0} pt
                </span>
              )}
              {showAttemptControls ? (
                <>
                  <button
                    type="button"
                    className={`forms-q-attempt-toggle${question.awardAttemptPoints ? ' forms-q-attempt-toggle--on' : ''}`}
                    onClick={toggleAttemptPoints}
                    title="Award points for wrong answers"
                  >
                    Attempt
                  </button>
                  {question.awardAttemptPoints ? (
                    editingAttemptPoints ? (
                      <input
                        type="number"
                        min="0"
                        step="1"
                        className="forms-q-points-input forms-q-points-input--attempt"
                        value={editAttemptPointsValue}
                        onChange={(e) => setEditAttemptPointsValue(e.target.value)}
                        onBlur={commitEditAttemptPoints}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            commitEditAttemptPoints();
                          }
                          if (e.key === 'Escape') {
                            setEditingAttemptPoints(false);
                          }
                        }}
                        aria-label="Attempt points for wrong answer"
                        autoFocus
                      />
                    ) : (
                      <span
                        className="forms-q-points-badge forms-q-points-badge--attempt"
                        onDoubleClick={startEditAttemptPoints}
                        title="Double-click to edit attempt points"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            startEditAttemptPoints();
                          }
                        }}
                      >
                        {Number(question.attemptPoints) || 0} pt
                      </span>
                    )
                  ) : null}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="forms-q-card-actions">
          {groupingOn && groupOptions?.length ? (
            <select
              value={question.group || groupOptions[0]}
              onChange={(e) => onUpdate('group', e.target.value)}
              className="forms-q-group-select"
              title="Question group"
              aria-label="Question group"
            >
              {groupOptions.map((g) => (
                <option key={g} value={g}>{g}</option>
              ))}
            </select>
          ) : null}
          <button
            type="button"
            onClick={() => onMove('up')}
            disabled={!canMoveUp}
            className="forms-icon-btn"
            title="Move up"
            aria-label="Move up"
          >
            ↑
          </button>
          <button
            type="button"
            onClick={() => onMove('down')}
            disabled={!canMoveDown}
            className="forms-icon-btn"
            title="Move down"
            aria-label="Move down"
          >
            ↓
          </button>
          <button
            type="button"
            onClick={onRequestDelete}
            className="forms-icon-btn forms-icon-btn--danger"
            title="Delete question"
            aria-label="Delete question"
          >
            ×
          </button>
        </div>
      </div>

      <div className="forms-q-grid">
        <div className="forms-q-field forms-q-field--full">
          <div className="forms-q-label-row">
            <span className="forms-q-field-label" id={labelFieldId}>Label *</span>
            <button
              type="button"
              className={[
                'forms-q-label-format-btn',
                labelRichOpen ? 'forms-q-label-format-btn--active' : '',
                !labelRichOpen && labelHasFormatting ? 'forms-q-label-format-btn--formatted' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => setLabelRichOpen((open) => !open)}
              aria-expanded={labelRichOpen}
              aria-controls={labelInputId}
              aria-label={labelRichOpen ? 'Hide rich text formatting' : 'Show rich text formatting'}
              title={labelRichOpen ? 'Plain label' : 'Rich text formatting'}
            >
              Aa
            </button>
          </div>
          {labelRichOpen ? (
            <RichTextEditor
              id={labelInputId}
              aria-labelledby={labelFieldId}
              value={question.label || ''}
              onChange={(html) => onUpdate('label', html)}
              placeholder="Question label — use bold, lists, etc."
            />
          ) : (
            <input
              id={labelInputId}
              type="text"
              className="forms-q-input"
              aria-labelledby={labelFieldId}
              value={labelPlainInputValue(question.label)}
              onChange={(e) => onUpdate('label', e.target.value)}
              placeholder="Question label"
            />
          )}
        </div>
        <div className="forms-q-field">
          <span className="forms-q-field-label" id={`q-type-label-${question.question_id || index}`}>Type *</span>
          <button
            type="button"
            className="forms-q-type-badge"
            onClick={() => setShowTypePicker(true)}
            aria-labelledby={`q-type-label-${question.question_id || index}`}
          >
            {selectedType.label}
          </button>
        </div>
      </div>

      {showTypePicker ? (
        <div
          className="forms-help-overlay forms-help-overlay--nested"
          role="presentation"
          onClick={() => setShowTypePicker(false)}
        >
          <div
            className="forms-help-dialog forms-type-picker"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`q-type-picker-title-${question.question_id || index}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="forms-help-header">
              <h3
                id={`q-type-picker-title-${question.question_id || index}`}
                className="forms-help-title"
              >
                Question type
              </h3>
              <button
                type="button"
                className="forms-sheet-close"
                onClick={() => setShowTypePicker(false)}
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="forms-help-body forms-type-picker-body">
              <p className="forms-type-picker-intro">
                Choose how respondents will answer this question.
              </p>
              <div className="forms-type-picker-grid">
                {fieldTypes.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    className={`forms-type-picker-card${(question.type || 'varchar') === opt.value ? ' forms-type-picker-card--active' : ''}`}
                    onClick={() => selectQuestionType(opt.value)}
                  >
                    <span className="forms-type-picker-card-title">{opt.label}</span>
                    <span className="forms-type-picker-card-desc">{opt.description}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {needsOptions ? (
        <div className="forms-q-options">
          <div className="forms-q-options-head">
            <span className="forms-q-field-label">Options *</span>
            <button type="button" onClick={onAddOption} className="forms-link-btn">
              + Add option
            </button>
          </div>
          {(question.options || []).map((option, optIndex) => (
            <div key={optIndex} className="forms-q-option-row">
              {showChoiceScoring ? (
                <label className="forms-q-correct-check" title="Mark as correct">
                  <input
                    type="checkbox"
                    checked={(scoring.correctOptions || []).map(String).includes(String(option))}
                    disabled={!option?.trim()}
                    onChange={() => onToggleCorrectOption(option)}
                  />
                  <span className="forms-q-correct-label">✓</span>
                </label>
              ) : null}
              <input
                type="text"
                value={option}
                onChange={(e) => onUpdateOption(optIndex, e.target.value)}
                placeholder={`Option ${optIndex + 1}`}
                className="forms-q-input"
              />
              <button
                type="button"
                onClick={() => onDeleteOption(optIndex)}
                className="forms-icon-btn forms-icon-btn--danger"
                title="Remove option"
                aria-label="Remove option"
              >
                ×
              </button>
            </div>
          ))}
          {(!question.options || question.options.length === 0) ? (
            <p className="forms-q-options-empty">Add at least one option.</p>
          ) : null}
        </div>
      ) : null}

      {UPLOAD_QUESTION_TYPES.has(question.type) ? (
        <label className="forms-q-check forms-q-check--inline">
          <input
            type="checkbox"
            checked={question.allowMultiple !== false}
            onChange={(e) => onUpdate('allowMultiple', e.target.checked)}
          />
          Allow multiple files
        </label>
      ) : null}

      {pointsMode ? (
        <div className="forms-q-scoring">
          {showAnswerModeSelect ? (
            <>
              <span className="forms-q-field-label">Answer scoring *</span>
              <div
                className="forms-type-radios forms-scoring-radios"
                role="radiogroup"
                aria-label="Answer scoring"
              >
                {allowedAnswerModes.map((value) => (
                  <label
                    key={value}
                    className={`forms-type-radio forms-type-radio--compact${answerMode === value ? ' forms-type-radio--active' : ''}`}
                  >
                    <input
                      type="radio"
                      name={`q-scoring-${question.question_id || index}`}
                      value={value}
                      checked={answerMode === value}
                      onChange={() => onUpdateScoring({ answerMode: value })}
                    />
                    <span>{ANSWER_MODE_LABELS[value]}</span>
                  </label>
                ))}
              </div>
            </>
          ) : (
            <span className="forms-q-field-label">
              Scoring: {ANSWER_MODE_LABELS[answerMode] || answerMode}
            </span>
          )}

          {showTextScoring ? (
            <div className="forms-q-text-rule">
              <select
                value={textRule.match}
                onChange={(e) => onUpdateScoring({
                  textRule: { ...textRule, match: e.target.value }
                })}
                className="forms-q-input"
              >
                {Object.entries(TEXT_MATCH_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
              <input
                type="text"
                value={textRule.value}
                onChange={(e) => onUpdateScoring({
                  textRule: { ...textRule, value: e.target.value }
                })}
                placeholder='e.g. "beautiful"'
                className="forms-q-input"
              />
              <label className="forms-q-check forms-q-check--inline">
                <input
                  type="checkbox"
                  checked={textRule.caseSensitive === true}
                  onChange={(e) => onUpdateScoring({
                    textRule: { ...textRule, caseSensitive: e.target.checked }
                  })}
                />
                Case sensitive
              </label>
            </div>
          ) : null}

          {showNumberExactScoring ? (
            <div className="forms-q-text-rule">
              <input
                type="number"
                value={numberRule.exact}
                onChange={(e) => onUpdateScoring({
                  numberRule: { ...numberRule, exact: e.target.value }
                })}
                placeholder="Correct value"
                className="forms-q-input"
              />
            </div>
          ) : null}

          {showNumberRangeScoring ? (
            <div className="forms-q-text-rule">
              <input
                type="number"
                value={numberRule.min}
                onChange={(e) => onUpdateScoring({
                  numberRule: { ...numberRule, min: e.target.value }
                })}
                placeholder="Min"
                className="forms-q-input"
                aria-label="Minimum value"
              />
              <span className="forms-q-range-sep">to</span>
              <input
                type="number"
                value={numberRule.max}
                onChange={(e) => onUpdateScoring({
                  numberRule: { ...numberRule, max: e.target.value }
                })}
                placeholder="Max"
                className="forms-q-input"
                aria-label="Maximum value"
              />
            </div>
          ) : null}

          {showDateExactScoring ? (
            <div className="forms-q-text-rule">
              <input
                type="date"
                value={dateRule.exact}
                onChange={(e) => onUpdateScoring({
                  dateRule: { ...dateRule, exact: e.target.value }
                })}
                className="forms-q-input forms-q-input--date"
                aria-label="Correct date"
              />
              <span className="forms-q-scoring-hint forms-q-scoring-hint--inline">mm/dd/yyyy</span>
            </div>
          ) : null}

          {showDateRangeScoring ? (
            <div className="forms-q-text-rule">
              <input
                type="date"
                value={dateRule.min}
                onChange={(e) => onUpdateScoring({
                  dateRule: { ...dateRule, min: e.target.value }
                })}
                className="forms-q-input forms-q-input--date"
                aria-label="From date"
              />
              <span className="forms-q-range-sep">to</span>
              <input
                type="date"
                value={dateRule.max}
                onChange={(e) => onUpdateScoring({
                  dateRule: { ...dateRule, max: e.target.value }
                })}
                className="forms-q-input forms-q-input--date"
                aria-label="To date"
              />
              <span className="forms-q-scoring-hint forms-q-scoring-hint--inline">mm/dd/yyyy</span>
            </div>
          ) : null}

          {answerMode === ANSWER_MODES.FREE ? (
            <p className="forms-q-scoring-hint">Any non-empty answer earns full points.</p>
          ) : null}
          {answerMode === ANSWER_MODES.MANUAL ? (
            <p className="forms-q-scoring-hint">
              Points are not auto-awarded. A grader reviews the response and assigns points manually.
            </p>
          ) : null}
          {showChoiceScoring ? (
            <p className="forms-q-scoring-hint">
              Check correct option(s) above.
              {question.type === 'multiple-choice'
                ? ' Student must select exactly the correct set to earn full points.'
                : ''}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

export default FormEditor;
